import { useSetEscalation } from "@/hooks/useSupportDepartments";
import type { Department } from "@/services/supportDepartmentsService";

import { AddedPersonRow } from "./AddedPersonRow";
import { AddPersonForm } from "./AddPersonForm";
import { DormantLeadRow } from "./DormantLeadRow";
import { MemberRow } from "./MemberRow";
import { RemovedPersonRow } from "./RemovedPersonRow";
import { SlackIdInput } from "./SlackIdInput";

export function DepartmentSection({ department }: { department: Department }) {
  const setEscalation = useSetEscalation();
  // Removed-in-Core members are listed under "Removed", not as members. The
  // override lists are optional: an older backend does not send them.
  const members = department.members.filter((m) => !m.excluded);
  const added = department.added ?? [];
  const removed = department.removed ?? [];
  const dormant = department.dormant_leads ?? [];

  return (
    <section
      aria-label={`${department.label} department`}
      className="rounded-md border border-border bg-card p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">{department.label}</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Escalation contact</span>
          <SlackIdInput
            label={`Escalation contact for ${department.label}`}
            value={department.escalation_slack_id}
            onSave={(slackId) => setEscalation.mutateAsync({ department: department.key, slackId })}
          />
        </div>
      </div>
      {members.length === 0 && added.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">
          No members. Tickets fall back to the escalation contact.
        </p>
      ) : (
        <ul className="mt-2 divide-y divide-border">
          {members.map((m) => (
            <MemberRow key={m.teamwork_person_id} member={m} department={department} />
          ))}
          {added.map((p) => (
            <AddedPersonRow key={p.id} person={p} department={department} />
          ))}
        </ul>
      )}
      {removed.length > 0 ? (
        <div className="mt-2">
          <p className="text-xs font-medium text-muted-foreground">Removed</p>
          <ul className="divide-y divide-border">
            {removed.map((o) => (
              <RemovedPersonRow key={o.id} override={o} department={department} />
            ))}
          </ul>
        </div>
      ) : null}
      {dormant.length > 0 ? (
        <ul className="mt-2 divide-y divide-border">
          {dormant.map((o) => (
            <DormantLeadRow key={o.id} override={o} department={department} />
          ))}
        </ul>
      ) : null}
      <AddPersonForm department={department} />
    </section>
  );
}
